import * as actionTypes from "./pricingActionTypes";
import * as actions from "./pricingActions";
import { createAction } from "@reduxjs/toolkit";
import { HandledError } from "common/errors";
import * as ids from "common/ids";
import * as logger from "common/logger";
import * as config from "config";
import * as errorActions from "store/actions/error/errorActions";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";
import * as submissionSelectors from "store/selectors/submission/submissionSelector";
import * as staticDataSelectors from "store/selectors/temp/staticData/staticDataSelectors";
import * as utils from "utils";

export const updateComment = (comment) => {
  return {
    type: actionTypes.UPDATE_LAYER_COMMENT,
    payload: comment,
  };
};

export const updateLayer = ({ layerId, values }) => {
  return {
    type: actionTypes.UPDATE_LAYER,
    payload: { layerId, values },
  };
};

// Normalise the `layer_defaults` config into { always, rules }. The config is a
// single JSON object where any top-level scalar `field: value` is an
// unconditional default (fill that field with the value), and `rules` holds
// conditional defaults:
//   {
//     "<field>": <value>, ...                            // unconditional
//     "rules": [ { "when": { "<field>": <value>, ... },  // all must match
//                  "set":  { "<field>": <value>, ... } } ]// filled if empty
//   }
export const normaliseLayerDefaults = (cfg = {}) => {
  const always = {};
  for (const [key, value] of Object.entries(cfg)) {
    if (key === "rules") continue;
    if (value !== null && typeof value !== "object") always[key] = value;
  }
  return { always, rules: Array.isArray(cfg.rules) ? cfg.rules : [] };
};

// Compute the (empty-only) field values to write to a layer from a normalised
// defaults config. `always` fills first, then `rules` are applied and re-applied
// until stable so a value one rule sets can satisfy another (cascading). Pure.
export const resolveLayerDefaultUpdates = (layer, { always, rules }) => {
  const working = { ...layer };
  const updates = {};
  const fillEmpty = (target) => {
    for (const [field, value] of Object.entries(target ?? {})) {
      if (value != null && working[field] == null) {
        working[field] = value;
        updates[field] = value;
      }
    }
  };
  fillEmpty(always);
  for (let pass = 0; pass < 10; pass += 1) {
    const before = Object.keys(updates).length;
    for (const rule of rules ?? []) {
      const matches = Object.entries(rule.when ?? {}).every(
        ([field, value]) => working[field] === value
      );
      if (matches) fillEmpty(rule.set);
    }
    if (Object.keys(updates).length === before) break;
  }
  return updates;
};

// Fill the active layer's config-driven defaults (from `layer_defaults`) when
// those fields are empty — applied on entering Layer Pricing / when a rule's
// source field changes, so a layer picks up defaults without an explicit "Add
// Layer". Non-destructive: only sets fields that currently have no value.
export const applyActiveLayerDefaults = (configOverride) => (
  dispatch,
  getState
) => {
  const state = getState();
  // Prefer the config passed in by the caller (fetched via useConfigQuery), and
  // fall back to the preloaded static-data bundle. `layer_defaults` is a newer
  // key that may not be in the preloaded bundle, so passing it in is what makes
  // the defaults reliably apply.
  const cfg =
    configOverride ??
    staticDataSelectors.selectConfig("layer_defaults")(state) ??
    {};
  const idx = pricingSelectors.getActiveLayerIndex(state);
  const layer = state.pricing?.layers?.[idx];
  if (!layer?.id) return;
  const updates = resolveLayerDefaultUpdates(
    layer,
    normaliseLayerDefaults(cfg)
  );
  if (Object.keys(updates).length > 0) {
    dispatch(updateLayer({ layerId: layer.id, values: updates }));
  }
};

export const updateActiveLayerLimit = (limit) => {
  return {
    type: actionTypes.UPDATE_ACTIVE_LAYER_LIMIT,
    payload: limit,
  };
};

export const updateActiveLayerAttachment = (attachment) => ({
  type: actionTypes.UPDATE_ACTIVE_LAYER_ATTACHMENT,
  payload: attachment,
});

export const updateActiveLayerLineSizeAbsolute = (line_size_absolute) => ({
  type: actionTypes.UPDATE_ACTIVE_LAYER_LINE_SIZE_ABSOLUTE,
  payload: line_size_absolute,
});

export const setActiveLayerPricingSelectedModel = createAction(
  "pricing/setActiveLayerPricingSelectedModel",
  ({ model }) => {
    return {
      payload: { model },
    };
  }
);

export const setActiveLayerPricingSelectedPrice = createAction(
  "pricing/setActiveLayerPricingSelectedPrice",
  ({ model, value }) => {
    return {
      payload: { model, value },
    };
  }
);

export const refreshActiveLayerPricing = () => (dispatch, getState) => {
  const state = getState();
  const backend = staticDataSelectors.selectAnalyticsConfig(state)?.layerPricing
    ?.backend;
  if (backend === "analytics") {
    return _refreshActiveLayerPricing_analytics(dispatch, getState);
  } else {
    return _refreshActiveLayerPricing_claims(dispatch, getState);
  }
};

export const _extractDefaultLayerPricingModel = ({
  layerPricingConfig,
  resolvedSubmission,
}) => {
  // Conceptually, this belongs in the backend. But to integrate this into the existing
  // flow for now, it's been implemented here.
  const isMatch = (predicate) => {
    if (!predicate) return true;
    return Object.entries(predicate).every(([key, expectedValue]) => {
      const parts = String(key).split(".");
      const value = parts.reduce(
        (accumulated, current) => accumulated?.[current],
        resolvedSubmission
      );
      return value === expectedValue;
    });
  };

  return (
    layerPricingConfig?.defaultModelByLayerPredicate?.find(({ predicate }) =>
      isMatch(predicate)
    )?.value ?? layerPricingConfig?.defaultModel
  );
};

const _refreshActiveLayerPricing_analytics = (dispatch, getState) => {
  dispatch({
    type: actionTypes.REFRESH_ACTIVE_LAYER_PRICING,
  });
  const state = getState();
  const layerPricingConfig =
    staticDataSelectors.selectAnalyticsConfig(state)?.layerPricing ?? {};
  const resolvedSubmission = submissionSelectors.selectResolvedSubmission(
    state
  );

  const featureFlags = staticDataSelectors.selectConfig("feature_flags")(state);
  const showLayerPricingErrorsInline =
    featureFlags?.["show-layer-pricing-errors-inline"] ?? false;

  const layer = pricingSelectors.getActiveLayer(state);
  const query = {
    limit: layer.limit,
    attachment: layer.attachment,
    targetLayer: layer.index,
  };

  Promise.all(
    layerPricingConfig.models.map((modelConfig) =>
      utils
        .authenticatedFetch(
          config.endpoints.analytics("calculate-submission-analytics"),
          {
            body: JSON.stringify({
              engine: modelConfig.engine ?? "default",
              metric: "layer_pricing",
              submission: resolvedSubmission,
            }),
            method: "post",
            headers: {
              "Content-Type": "application/json",
            },
          }
        )
        .then((result) => result.json())
    )
  )
    .then((results) => {
      const successfulResults = [];
      const failedModels = [];

      results.forEach((result, index) => {
        if (result.failure) {
          failedModels.push({
            model: layerPricingConfig.models[index],
            failure: result.failure,
          });
        } else {
          successfulResults.push({
            result,
            model: layerPricingConfig.models[index],
          });
        }
      });

      if (successfulResults.length > 0 || showLayerPricingErrorsInline) {
        const prices = Object.fromEntries([
          ...successfulResults.map(({ result, model }) => [
            model.key,
            result.values,
          ]),
          ...failedModels.map(({ model, failure }) => [
            model.key,
            { error: failure },
          ]),
        ]);

        const selectedMeasures = Object.fromEntries(
          layerPricingConfig.models
            .filter(
              (model) =>
                showLayerPricingErrorsInline ||
                !failedModels.some((fm) => fm.model.key === model.key)
            )
            .map((model) => [model.key, model.selectedPrice?.defaultMeasure])
        );

        const defaultModel = _extractDefaultLayerPricingModel({
          layerPricingConfig,
          resolvedSubmission,
        });

        dispatch(
          actions.activeLayerPricingReturned({
            query,
            prices,
            selectedMeasures,
            defaultModel,
          })
        );
      } else {
        dispatch(
          actions.activeLayerPricingFailed(
            query,
            "Failed to calculate layer.",
            { failures: failedModels.map((fm) => fm.failure) }
          )
        );
      }

      if (failedModels.length > 0 && !showLayerPricingErrorsInline) {
        const failureMessages = failedModels.map(
          ({ model, failure }) => `Model "${model.name}": ${failure}`
        );

        dispatch(
          errorActions.handleError(null, {
            title:
              failedModels.length === results.length
                ? "Unable to calculate pricing."
                : `${failedModels.length} pricing model(s) failed to calculate.`,
            message: failureMessages.join("\n"),
            type: "PRICING_ERROR",
          })
        );
      }
    })
    .catch((error) => {
      if (error.status === 400) {
        dispatch(
          errorActions.handleError(null, {
            title: "Unable to calculate pricing.",
            message: error.description,
            type: "PRICING_ERROR",
          })
        );
      }
      dispatch(
        actions.activeLayerPricingFailed(
          query,
          "Failed to understand response from server.",
          error
        )
      );
    });
};

const _refreshActiveLayerPricing_claims = (dispatch, getState) => {
  dispatch({
    type: actionTypes.REFRESH_ACTIVE_LAYER_PRICING,
  });
  const state = getState();
  const layer = pricingSelectors.getActiveLayer(state);
  const query = {
    limit: layer.limit,
    attachment: layer.attachment,
    targetLayer: layer.index,
  };

  const purePremiumKeyToPricingKeyMap = {
    [config.PURE_BURN_COST_KEY]: "burningCost",
    [config.ILF_FROM_BASE_KEY]: "ilfFromBase",
    [config.PURE_MODEL]: "modelPrice",
    [config.SCALED_EXPERIENCE]: "pricingModelledLossCost",
    [config.RECOMMENDED_BLEND]: "recommendedPrice",
    [config.UW_ADJUSTED]: "uwAdjustedPrice",
  };

  utils
    .authenticatedFetch(config.endpoints.claims("pricing/pure-premium"), {
      body: JSON.stringify(pricingSelectors.purePremiumRequest(state)),
      method: "post",
      headers: {
        "Content-Type": "application/json",
      },
    })
    .then((result) => result.json())
    .then((data) => {
      dispatch(
        actions.activeLayerPricingReturned({
          query,
          prices: {
            __v1__: Object.fromEntries(
              Object.entries(data)
                .filter(([key, _]) => key in purePremiumKeyToPricingKeyMap)
                .map(([key, value]) => [
                  purePremiumKeyToPricingKeyMap[key],
                  value,
                ])
            ),
          },
          selectedMeasures: {
            __v1__: "uwAdjustedPrice",
          },
          defaultModel: "__v1__",
        })
      );
    })
    .catch((error) => {
      if (error.status === 400) {
        dispatch(
          errorActions.handleError(null, {
            title: "Unable to calculate pricing.",
            message: error.description,
            type: "PURE_PREMIUM_PRICING_ERROR",
          })
        );
      }
      dispatch(
        actions.activeLayerPricingFailed(
          query,
          "Failed to understand response from server.",
          error
        )
      );
    });
};

export const activeLayerPricingReturned = ({
  query,
  prices,
  selectedMeasures,
  defaultModel,
}) => ({
  type: actionTypes.ACTIVE_LAYER_PRICING_RETURNED,
  payload: {
    query,
    prices,
    selectedMeasures,
    defaultModel,
  },
});

export const activeLayerPricingFailed = (query, message, error) => {
  logger.exception(error);
  return {
    type: actionTypes.ACTIVE_LAYER_PRICING_FAILED,
    payload: {
      ...query,
      message,
    },
  };
};

export const updateBaseLayerLimit = (limit) => ({
  type: actionTypes.UPDATE_BASE_LAYER_LIMIT,
  payload: limit,
});

export const updateBaseLayerFrom = (from) => {
  return {
    type: actionTypes.UPDATE_BASE_LAYER_FROM,
    payload: from,
  };
};

export const updateBaseLayerTo = (to) => {
  return {
    type: actionTypes.UPDATE_BASE_LAYER_TO,
    payload: to,
  };
};

export const updateBaseLayerAttachment = (attachment) => ({
  type: actionTypes.UPDATE_BASE_LAYER_ATTACHMENT,
  payload: attachment,
});

export const updateActiveLayerGrossPremium = (premium) => ({
  type: actionTypes.UPDATE_GROSS_PREMIUM,
  payload: premium,
});

export const updateActiveLayerGrossPPM = (ppm) => ({
  type: actionTypes.UPDATE_ACTIVE_LAYER_GROSS_PPM,
  payload: ppm,
});

export const updateActiveLayerShareOfLine = (shareOfLine) => ({
  type: actionTypes.UPDATE_ACTIVE_LAYER_SHARE_OF_LINE,
  payload: shareOfLine,
});

export const updateActiveLayerTRIA = (TRIA) => ({
  type: actionTypes.UPDATE_ACTIVE_LAYER_TRIA,
  payload: TRIA,
});

export const updateActiveLayerShareOfPremium = (shareOfPremium) => ({
  type: actionTypes.UPDATE_ACTIVE_LAYER_SHARE_OF_PREMIUM,
  payload: shareOfPremium,
});

export const updateActiveLayerAuthorizedLimit = (authorizedLimit) => ({
  type: actionTypes.UPDATE_ACTIVE_LAYER_AUTHORIZED_LIMIT,
  payload: authorizedLimit,
});

export const updateActiveLayerBrokerage = (brokerage) => ({
  type: actionTypes.UPDATE_ACTIVE_LAYER_BROKERAGE,
  payload: brokerage,
});

export const updateActiveLayerOtherAcquisitionCosts = (costs) => ({
  type: actionTypes.UPDATE_ACTIVE_LAYER_OTHER_ACQUISITION_COSTS,
  payload: costs,
});

export const updateActiveLayerProfit = (profit) => ({
  type: actionTypes.UPDATE_ACTIVE_LAYER_PROFIT,
  payload: profit,
});

export const updateActiveLayerExpenses = (expenses) => ({
  type: actionTypes.UPDATE_ACTIVE_LAYER_EXPENSES,
  payload: expenses,
});

export const updateInsuredWeight = (weight) => ({
  type: actionTypes.UPDATE_INSURED_WEIGHT,
  payload: weight,
});

export const updateImprovexWeight = (weight) => ({
  type: actionTypes.UPDATE_IMPROVEX_WEIGHT,
  payload: weight,
});

export const updateChosenExposureMetric = (metric) => ({
  type: actionTypes.UPDATE_CHOSEN_EXPOSURE_METRIC,
  payload: metric,
});

export const updateBaseLayerExpectedNumberOfBlendedLosses = (
  numberOfEvents
) => ({
  type: actionTypes.UPDATE_BASE_LAYER_EXPECTED_NUMBER_OF_BLENDED_LOSSES,
  payload: numberOfEvents,
});

export const updateNumberOfEventsAtInsuredImpactingBaseLayer = (
  numberOfEvents
) => ({
  type: actionTypes.UPDATE_NUMBER_OF_EVENTS_AT_INSURED_IMPACTING_BASE_LAYER,
  payload: numberOfEvents,
});

export const makeRecalculateInsuredExpectedEventsBody = (state) => {
  const naicsCode = state?.input?.program?.naics?.code ?? null;
  const naicsOptional = pricingSelectors.selectNaicsCodeIsOptional(state);
  if (naicsCode == null && !naicsOptional) {
    throw new HandledError({
      title: "MISSING_NAICS_CODE",
      message: "A NAICS Code is required.",
    });
  }
  const baseLayer = {
    limit: state.pricing?.baseLayer?.limit,
    attachment: state.pricing?.baseLayer?.attachment,
  };
  if (baseLayer.limit == null || baseLayer.attachment == null) {
    throw new HandledError({
      title: "MISSING_BASE_LAYER",
      message: "A base layer is required.",
    });
  }
  return {
    averagesOverLastYears: [6, 3],
    exposureSeries: utils.exposureSeries(
      state.input.exposure.data,
      state.pricing.selectedExposureMetric.key
    ),
    turnoverCategory: state.input.program.turnoverCategory,
    baseLayer,
    naicsCode,
    currentTurnover: utils.latestTurnover(state.input.exposure.data),
    in_US: state.input.program.inUS,
    claimsQuery: pricingSelectors.selectPricingClaims(state),
  };
};

export const recalculateInsuredExpectedEvents = () => ({
  type: actionTypes.RECALCULATE_INSURED_EXPECTED_EVENTS,
});

export const updateActive = (index) => ({
  type: actionTypes.UPDATE_ACTIVE_LAYER,
  payload: index,
});

export const deleteLayer = (index) => ({
  type: actionTypes.DELETE_LAYER,
  payload: index,
});

export const addLayer = (initialData = {}) => (dispatch, getState) => {
  const state = getState();
  dispatch({
    type: actionTypes.ADD_LAYER,
    payload: { state, initialData },
  });
};

export const updateActiveLayerReference = (reference) => ({
  type: actionTypes.UPDATE_ACTIVE_LAYER_REFERENCE,
  payload: reference,
});

export const updateActiveLayerStatus = (status) => ({
  type: actionTypes.UPDATE_ACTIVE_LAYER_STATUS,
  payload: status,
});

export const updateActiveLayerPaymentTerms = (paymentTerms) => ({
  type: actionTypes.UPDATE_ACTIVE_LAYER_PAYMENT_TERMS,
  payload: paymentTerms ?? null,
});

export const updateActiveLayerReinsurance = (reinsurance) => ({
  type: actionTypes.UPDATE_ACTIVE_LAYER_REINSURANCE,
  payload: reinsurance,
});

export const updateAllLayersReinsurance = (reinsurance) => ({
  type: actionTypes.UPDATE_ALL_LAYERS_REINSURANCE,
  payload: reinsurance,
});

export const setAsBaseLayerForTowerPricingScaling = (index) => ({
  type: actionTypes.SET_AS_BASE_LAYER_FOR_TOWER_PRICING_SCALING,
  payload: index,
});

export const updateLayerStatus = (index, status) => ({
  type: actionTypes.UPDATE_LAYER_STATUS,
  payload: { index, status },
});

export const createPolicy = ({ name, layers = [] }) => ({
  type: actionTypes.CREATE_POLICY,
  payload: { name, layers },
});

export const deletePolicy = (id) => ({
  type: actionTypes.DELETE_POLICY,
  payload: { id },
});

export const updatePolicy = ({ id, name, layers }) => ({
  type: actionTypes.UPDATE_POLICY,
  payload: { id, name, layers },
});

export const setActivePolicy = (id) => ({
  type: actionTypes.SET_ACTIVE_POLICY,
  payload: { id },
});

export const updateLayerPricingNote = (note) => ({
  type: actionTypes.UPDATE_LAYER_PRICING_NOTE,
  payload: note,
});

export const updateFrequencyPricingNote = (note) => ({
  type: actionTypes.UPDATE_FREQUENCY_PRICING_NOTE,
  payload: note,
});

export const updateActiveLayerMinimumEarnedPremium = (
  minimumEarnedPremium
) => ({
  type: actionTypes.UPDATE_MINIMUM_EARNED_PREMIUM,
  payload: minimumEarnedPremium,
});

export const setActiveLayerPolicyForm = (policyForm) => ({
  type: actionTypes.SET_ACTIVE_LAYER_POLICY_FORM,
  payload: policyForm,
});

export const addPremiumAdjustment = createAction(
  "pricing/addPremiumAdjustment",
  ({ layerId }) => {
    return {
      payload: {
        layerId,
        premiumAdjustment: {
          id: ids.makeNewId(),
        },
      },
    };
  }
);

export const createPremiumAdjustment = createAction(
  "pricing/createPremiumAdjustment",
  ({ layerId, type, endDate, changeInPremium, changeInGrossPremium }) => {
    return {
      payload: {
        layerId,
        premiumAdjustment: {
          id: ids.makeNewId(),
          type,
          endDate,
          shareOfGrossPremiumChange: changeInPremium,
          grossPremiumChange: changeInGrossPremium,
        },
      },
    };
  }
);

export const editPremiumAdjustment = createAction(
  "pricing/editPremiumAdjustment",
  ({ layerId, premiumAdjustmentId, edits }) => {
    return {
      payload: { layerId, premiumAdjustmentId, edits },
    };
  }
);

export const deletePremiumAdjustment = createAction(
  "pricing/deletePremiumAdjustment",
  ({ layerId, premiumAdjustmentId }) => {
    return {
      payload: { layerId, premiumAdjustmentId },
    };
  }
);

export const deleteActiveLayerCustomOverrides = createAction(
  "pricing/deleteActiveLayerCustomOverrides"
);

export const deletePremiumAdjustmentCustomOverrides = createAction(
  "pricing/deletePremiumAdjustmentCustomOverrides",
  ({ layerId, premiumAdjustmentId }) => {
    return {
      payload: { layerId, premiumAdjustmentId },
    };
  }
);

export const updatePricingInput = ({ key, layerId = null, values }) => {
  return {
    type: actionTypes.UPDATE_PRICING_INPUT,
    payload: { key, layerId, values },
  };
};

export const setPricingAssumption = (name, value) => ({
  type: actionTypes.SET_PRICING_ASSUMPTION,
  payload: { [name]: value },
});

export const updateClaimsDateRange = ({ start, end }) => (dispatch) => {
  dispatch({
    type: actionTypes.UPDATE_CLAIMS_DATE_RANGE,
    payload: { start, end },
  });
};

export const updateClaimsIncludedLossTypes = (includedLossTypes) => (
  dispatch
) => {
  dispatch({
    type: actionTypes.UPDATE_CLAIMS_INCLUDED_LOSS_TYPES,
    payload: includedLossTypes,
  });
};
